import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listOrders } from '../api/orders';
import { ApiError } from '../api/client';
import type { ApiOrderStatus, FlowType, ListOrdersData, Order } from '../types/api';
import { formatAmount, formatDateTPE, FLOW_LABEL } from '../lib/format';
import { StatusBadge } from '../components/StatusBadge';

type StatusFilter = ApiOrderStatus | 'all' | '';
type FlowFilter = FlowType | '';

const PAGE_SIZE = 20;

export function OrdersListPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [flowFilter, setFlowFilter] = useState<FlowFilter>('');
  const [searchQ, setSearchQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [offset, setOffset] = useState(0);
  // 預設隱藏「已關閉 / 已過期」，減少列表雜訊；若 user 明確在下拉選了這兩個狀態則自動失效
  const [hideInactive, setHideInactive] = useState(true);

  const [data, setData] = useState<ListOrdersData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // q debounce 300ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  // 篩選變更時重置分頁
  useEffect(() => {
    setOffset(0);
  }, [statusFilter, flowFilter, debouncedQ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listOrders({
      status: statusFilter || undefined,
      flow: flowFilter || undefined,
      q: debouncedQ || undefined,
      limit: PAGE_SIZE,
      offset,
    })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message;
        setError(msg);
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, flowFilter, debouncedQ, offset]);

  const rawItems = data?.items ?? [];
  const total = data?.pagination.total ?? 0;
  const hasMore = data?.pagination.hasMore ?? false;
  const page = useMemo(() => Math.floor(offset / PAGE_SIZE) + 1, [offset]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  // 套 hideInactive 前端過濾；user 明確選 closed/expired 時不過濾（否則會空）
  const filterActive = hideInactive && statusFilter !== 'closed' && statusFilter !== 'expired';
  const items = useMemo(
    () =>
      filterActive
        ? rawItems.filter((o) => o.status !== 'closed' && o.status !== 'expired')
        : rawItems,
    [rawItems, filterActive],
  );
  const hiddenCount = rawItems.length - items.length;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-gray-900">訂單列表</h1>
        <Link
          to="/new"
          className="rounded bg-stamp px-4 py-2 text-sm font-medium text-white hover:bg-rose-800"
        >
          + 新增訂單
        </Link>
      </div>

      {/* 篩選列 */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border bg-white p-3">
        <FilterField label="狀態">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="">未付款 / 已付款 / 已關閉</option>
            <option value="pending">待付款</option>
            <option value="paid">已付款</option>
            <option value="closed">已關閉</option>
            <option value="expired">已過期</option>
            <option value="failed">失敗</option>
            <option value="all">全部（含失敗）</option>
          </select>
        </FilterField>

        <FilterField label="流程">
          <select
            value={flowFilter}
            onChange={(e) => setFlowFilter(e.target.value as FlowFilter)}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="">全部</option>
            <option value="A">A — API 取號</option>
            <option value="B">B — UPP 連結</option>
          </select>
        </FilterField>

        <FilterField label="搜尋">
          <input
            type="search"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="客戶名 / 訂單編號 / 品名"
            className="w-56 rounded border border-gray-300 px-2 py-1.5 text-sm"
          />
        </FilterField>

        <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={hideInactive}
            onChange={(e) => setHideInactive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span>隱藏已關閉 / 已過期</span>
        </label>
      </div>

      {/* 結果 */}
      {loading && items.length === 0 && (
        <div className="rounded border bg-white p-6 text-center text-sm text-gray-500">載入中…</div>
      )}

      {error && (
        <div className="rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          載入失敗：{error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="rounded border bg-white p-8 text-center text-sm text-gray-500">
          沒有符合條件的訂單。
        </div>
      )}

      {items.length > 0 && (
        <>
          {/* 桌機：表格 */}
          <div className="hidden overflow-hidden rounded-lg border bg-white md:block">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-3 py-2">建立時間</th>
                  <th className="px-3 py-2">訂單編號</th>
                  <th className="px-3 py-2">客戶</th>
                  <th className="px-3 py-2">品項</th>
                  <th className="px-3 py-2">流程</th>
                  <th className="px-3 py-2 text-right">金額</th>
                  <th className="px-3 py-2">期限</th>
                  <th className="px-3 py-2">狀態</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((o) => (
                  <OrderRow key={o.id} order={o} />
                ))}
              </tbody>
            </table>
          </div>

          {/* 手機：卡片 */}
          <div className="grid gap-2 md:hidden">
            {items.map((o) => (
              <OrderCard key={o.id} order={o} />
            ))}
          </div>

          {/* 分頁 */}
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              共 {total} 筆 · 第 {page} / {totalPages} 頁
              {hiddenCount > 0 && (
                <span className="ml-2 text-xs text-gray-400">（本頁隱藏 {hiddenCount} 筆已關閉/已過期）</span>
              )}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50 hover:enabled:bg-gray-100"
              >
                上一頁
              </button>
              <button
                type="button"
                disabled={!hasMore}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50 hover:enabled:bg-gray-100"
              >
                下一頁
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-500">
      <span>{label}</span>
      {children}
    </label>
  );
}

function OrderRow({ order }: { order: Order }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="whitespace-nowrap px-3 py-2 text-gray-500">{formatDateTPE(order.createdAt)}</td>
      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
        <Link to={`/orders/${order.id}`} className="text-blue-600 hover:underline">
          {order.merTradeNo}
        </Link>
      </td>
      <td className="whitespace-nowrap px-3 py-2">{order.customerName}</td>
      <td className="px-3 py-2 text-gray-700">{order.prodDesc}</td>
      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
        {FLOW_LABEL[order.flowType]}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right font-medium">
        {formatAmount(order.amount)}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-gray-500">{formatDateTPE(order.expireAt)}</td>
      <td className="whitespace-nowrap px-3 py-2">
        <StatusBadge status={order.status} />
      </td>
    </tr>
  );
}

function OrderCard({ order }: { order: Order }) {
  return (
    <Link
      to={`/orders/${order.id}`}
      className="rounded-lg border bg-white p-3 shadow-sm hover:bg-gray-50"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm font-medium text-gray-900">{order.customerName}</div>
          <div className="mt-0.5 text-xs text-gray-500">{order.prodDesc}</div>
        </div>
        <StatusBadge status={order.status} />
      </div>
      <div className="mt-2 flex items-center justify-between text-sm">
        <span className="font-semibold">{formatAmount(order.amount)}</span>
        <span className="text-xs text-gray-500">期限 {formatDateTPE(order.expireAt)}</span>
      </div>
      <div className="mt-1 font-mono text-[10px] text-gray-400">{order.merTradeNo}</div>
    </Link>
  );
}
